import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, X, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LS_KEY = "lb-onboarding-dismissed-v1";

/**
 * Dashboard onboarding checklist — a light activation nudge for new users.
 *
 * Steps are derived from live profile + platform state (never a "did you
 * click this button" checkbox). Once every step is complete, or the user
 * dismisses the panel, it hides for good (dismissal stored in localStorage,
 * so admins can still trigger it via a clean profile).
 */
export default function OnboardingChecklist() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [counts, setCounts] = useState({ jobs: 0, applications: 0, messages: 0 });

  useEffect(() => {
    try {
      if (localStorage.getItem(LS_KEY) === "1") setDismissed(true);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user || dismissed) return;
    let alive = true;
    const load = async () => {
      try {
        const roles = user.roles || [];
        const [jobs, apps, threads] = await Promise.all([
          roles.includes("author")
            ? api.get("/jobs", { params: { mine: true } }).then((r) => r.data?.length ?? 0).catch(() => 0)
            : Promise.resolve(0),
          roles.includes("beta_reader")
            ? api.get("/jobs/applications/mine/list").then((r) => r.data?.length ?? 0).catch(() => 0)
            : Promise.resolve(0),
          api.get("/threads").then((r) => r.data?.length ?? 0).catch(() => 0),
        ]);
        if (alive) setCounts({ jobs, applications: apps, messages: threads });
      } catch (e) { /* silent */ }
    };
    load();
    // Re-count when the user comes back to the tab (e.g. after posting a
    // project in a new tab, or on Dashboard remount after navigation). This
    // makes the "Post your first project" step turn green immediately.
    const onFocus = () => { if (alive) load(); };
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [user, dismissed]);

  const steps = useMemo(() => {
    if (!user) return [];
    const p = user.profile || {};
    const roles = user.roles || [];
    const isAuthor = roles.includes("author");
    const isReader = roles.includes("beta_reader");
    const list = [
      {
        id: "photo",
        title: "Add a profile photo",
        done: !!p.avatar_url,
        to: "/profile",
        cta: "Upload photo",
      },
      {
        id: "bio",
        title: "Write a short bio (about 2–3 sentences)",
        done: (p.bio || "").trim().length >= 40,
        to: "/profile",
        cta: "Add bio",
      },
      {
        id: "headline",
        title: "Add a headline (e.g. \"Fantasy author\" or \"Romance beta reader\")",
        done: (p.headline || "").trim().length >= 3,
        to: "/profile",
        cta: "Add headline",
      },
    ];
    if (isAuthor) {
      // "Post your first project" intentionally omitted — the gold
      // FirstProjectHint card on the Dashboard already covers this
      // action with a more inviting layout. Two competing CTAs pointing
      // at the same URL created a "which one?" moment for new authors.
    }
    if (isReader) {
      list.push({
        id: "first-application",
        title: "Apply to your first project",
        done: counts.applications > 0,
        to: "/jobs",
        cta: "Browse projects",
      });
    }
    list.push({
      id: "first-message",
      title: "Introduce yourself to another member",
      done: counts.messages > 0,
      to: "/search",
      cta: "Discover people",
    });
    return list;
  }, [user, counts]);

  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(LS_KEY, "1"); } catch (e) { /* ignore */ }
    toast.success("Checklist hidden. You can always reach the linked pages from the sidebar.");
  };

  if (!user || dismissed || total === 0 || done === total) return null;

  return (
    <div
      className="card-soft mt-8"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--sb-gold))]">
            <Sparkles size={12} /> Getting started
          </div>
          <h2 className="mt-1 font-serif text-2xl">Set up your Last Bookmark corner</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A few small steps that meaningfully improve how other users find and trust you.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs text-muted-foreground hover:text-foreground p-1 -mt-1 -mr-1"
          aria-label="Hide onboarding checklist"
          data-testid="onboarding-dismiss"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="font-medium">{done} of {total} complete</span>
          <span className="text-muted-foreground">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--sb-forest))] transition-all"
            style={{ width: `${pct}%` }}
            data-testid="onboarding-progress"
          />
        </div>
      </div>

      <ul className="mt-5 space-y-2" data-testid="onboarding-steps">
        {steps.map((s) => (
          <li
            key={s.id}
            className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
              s.done ? "bg-[hsl(var(--sb-cream-2))]/60 border-border" : "hover:bg-[hsl(var(--sb-cream-2))]/40"
            }`}
            data-testid={`onboarding-step-${s.id}`}
          >
            <span
              className={`shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full ${
                s.done
                  ? "bg-[hsl(var(--sb-forest))] text-white"
                  : "border border-border text-muted-foreground"
              }`}
            >
              {s.done ? <Check size={12} /> : <Circle size={10} />}
            </span>
            <div className="min-w-0 flex-1">
              <div className={`text-sm ${s.done ? "line-through text-muted-foreground" : "font-medium"}`}>
                {s.title}
              </div>
            </div>
            {!s.done && (
              <Link
                to={s.to}
                className="shrink-0 text-xs font-medium text-[hsl(var(--sb-gold))] inline-flex items-center gap-0.5 hover:underline"
                data-testid={`onboarding-step-${s.id}-cta`}
              >
                {s.cta} <ChevronRight size={12} />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
